/**
 * CDN umbrella + provider hooks for the Multi-CDN Orchestration BE endpoints.
 *
 * Two envelope shapes (confirmed by curl):
 *   GET /api/v3/cdn/umbrellas   → enveloped: { data: { total, umbrellas: [...] } }
 *   GET /api/v3/services/:id/cdn/providers → bare array: [...]
 *   PUT /api/v3/services/:id/cdn/providers/:provider/status → { service_id, provider, status }
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getHttpClient } from '@vnetwork/margarita-core/http';

const serviceApiV3 = getHttpClient('serviceApiV3');

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** One backend provider grouped under an orchestrator (canonical + children). */
export interface CdnUmbrellaProvider {
  provider_name: string;
  scope: string;
  is_canonical: boolean;
  status: string;
  capabilities?: CdnProviderCapabilities;
}

export interface CdnUmbrella {
  service_id: string;
  service_name: string;
  status: string;
  provider_name: string;
  /** Provider scope label, e.g. "raven", "eagle". */
  scope: string;
  project_id?: string | null;
  /** All provider backends this orchestrator groups (canonical first). */
  providers?: CdnUmbrellaProvider[];
  provider_count?: number;
  /** Org that owns the orchestrator (differs from caller when shared-in). */
  owner_org_id?: string;
  /** True when surfaced via a team_share from another org (badge + pinned top). */
  is_shared?: boolean;
}

export interface CdnUmbrellasResult {
  total: number;
  umbrellas: CdnUmbrella[];
}

/**
 * Per-provider capability data returned by the BE.
 *
 * `aspects` maps each CDN feature area (e.g. "tls", "waf", "upstream") to
 * a boolean indicating whether the provider's handler implements it.
 * Missing key = treat as false (same as explicit false).
 *
 * `upstream_unsupported_fields` lists the CDNOriginConfig JSON field names
 * that this provider's UpdateUpstream rejects. Empty array = all fields accepted.
 */
export interface CdnProviderCapabilities {
  aspects: Record<string, boolean>;
  upstream_unsupported_fields: string[];
}

/** One backend provider entry returned by GET /services/:id/cdn/providers. */
export interface CdnProvider {
  service_id: string;
  provider_name: string;
  scope: string;
  is_canonical: boolean;
  status: 'active' | 'suspended';
  health: 'active' | 'degraded' | 'unknown';
  health_message?: string;
  probed_at: number;
  /** Capability info: which aspects the handler implements and which upstream fields it rejects. */
  capabilities?: CdnProviderCapabilities;
}

export interface ToggleProviderResult {
  service_id: string;
  provider: string;
  status: string;
}

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const cdnProviderKeys = {
  umbrellas: (projectId?: string) =>
    ['cdn', 'umbrellas', projectId ?? ''] as const,
  providers: (serviceId: string) =>
    ['cdn', 'service', serviceId, 'providers'] as const,
};

// ---------------------------------------------------------------------------
// useCdnUmbrellas — GET /api/v3/cdn/umbrellas
// Response is enveloped: res.data.data.umbrellas
// ---------------------------------------------------------------------------

export function useCdnUmbrellas(projectId?: string, opts?: { enabled?: boolean }) {
  return useQuery({
    queryKey: cdnProviderKeys.umbrellas(projectId),
    enabled: opts?.enabled ?? true,
    retry: false,
    queryFn: async (): Promise<CdnUmbrellasResult> => {
      const params = new URLSearchParams();
      if (projectId) params.set('project_id', projectId);
      const qs = params.toString();
      const res = await serviceApiV3.get<{ data: CdnUmbrellasResult }>(
        `/cdn/umbrellas${qs ? `?${qs}` : ''}`,
      );
      // Envelope: res.data.data.{ total, umbrellas }
      return res.data?.data ?? { total: 0, umbrellas: [] };
    },
  });
}

// ---------------------------------------------------------------------------
// useCdnProviders — GET /api/v3/services/:id/cdn/providers
// Response is a bare array (NOT enveloped).
// ---------------------------------------------------------------------------

export function useCdnProviders(serviceId: string) {
  return useQuery({
    queryKey: cdnProviderKeys.providers(serviceId),
    enabled: !!serviceId,
    retry: false,
    queryFn: async (): Promise<CdnProvider[]> => {
      const res = await serviceApiV3.get<CdnProvider[]>(
        `/services/${serviceId}/cdn/providers`,
      );
      // Bare array — res.data is already the array.
      return Array.isArray(res.data) ? res.data : [];
    },
  });
}

// ---------------------------------------------------------------------------
// useAssignCdnProject — PATCH /api/v3/services/:id/cdn/project
// Sets (or clears) services.project_id on the canonical umbrella.
// Invalidates the umbrella list so the home page regroups.
// ---------------------------------------------------------------------------

interface AssignProjectParams {
  /** Project UUID to assign, or null to unassign. */
  projectId: string | null;
}

export interface AssignProjectResult {
  service_id: string;
  project_id: string | null;
}

export function useAssignCdnProject(serviceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ projectId }: AssignProjectParams): Promise<AssignProjectResult> => {
      const res = await serviceApiV3.patch<AssignProjectResult>(
        `/services/${serviceId}/cdn/project`,
        { project_id: projectId },
      );
      return res.data;
    },
    onSuccess: () => {
      // Invalidate all umbrella queries so the home page regroups immediately.
      qc.invalidateQueries({ queryKey: ['cdn', 'umbrellas'] });
    },
  });
}

// ---------------------------------------------------------------------------
// useRenameCdnService — PATCH /api/v3/services/:id/cdn/name
// Updates the orchestrator's display name on the canonical umbrella row. Does
// NOT touch the provider. Invalidates the umbrella list so the CDN home and
// settings reflect the new name.
// ---------------------------------------------------------------------------

export interface RenameServiceResult {
  service_id: string;
  service_name: string;
}

export function useRenameCdnService(serviceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (name: string): Promise<RenameServiceResult> => {
      const res = await serviceApiV3.patch<RenameServiceResult>(
        `/services/${serviceId}/cdn/name`,
        { name },
      );
      return res.data;
    },
    onSuccess: () => {
      // Refresh all umbrella queries so the home page + settings show the new name.
      qc.invalidateQueries({ queryKey: ['cdn', 'umbrellas'] });
    },
  });
}

// ---------------------------------------------------------------------------
// useDeleteCdnService — DELETE /api/v3/services/:id/cdn
// Soft-deletes the orchestrator family (canonical + provider children →
// "terminated" on the BE, audit-logged). The BE refuses with 409 while any
// domain is still bound. Invalidates every cdn-scoped cache on success — the
// service must vanish from the home page, command menu and service lists.
// ---------------------------------------------------------------------------

export interface DeleteCdnServiceResult {
  canonical_id: string;
  service_name: string;
  deleted_service_ids: string[];
  deactivated_zone_ids: string[];
}

export function useDeleteCdnService(serviceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<DeleteCdnServiceResult> => {
      const res = await serviceApiV3.delete<{ data: DeleteCdnServiceResult }>(
        `/services/${serviceId}/cdn`,
      );
      return res.data?.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['cdn'] });
      qc.invalidateQueries({ queryKey: ['services'] });
    },
  });
}

// ---------------------------------------------------------------------------
// useToggleCdnProvider — PUT /api/v3/services/:id/cdn/providers/:provider/status
// Invalidates providers list + umbrella list on success.
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// useUpstreamProviderLimits
//
// Derives which ENABLED secondary providers have upstream_unsupported_fields,
// so the OriginTab can surface a single, non-blocking warning listing the
// affected fields and the providers that don't support them.
//
// Returns an empty array when serviceId is absent (domain accessed outside a
// service context — the alert is simply omitted, not an error).
// ---------------------------------------------------------------------------

export interface ProviderUpstreamLimit {
  /** Display-safe provider codename (e.g. "eagle"). Never a vendor brand. */
  providerName: string;
  /** JSON field names from CDNOriginConfig that this provider rejects on UpdateUpstream. */
  unsupportedFields: string[];
}

/**
 * Returns the list of enabled secondary providers that have known upstream field
 * limitations. Callers use this to display a single informational Alert on the
 * Origin tab without redesigning the form.
 *
 * @param serviceId - Umbrella service ID. When undefined the hook is disabled and
 *   returns an empty array (graceful no-op).
 */
export function useUpstreamProviderLimits(serviceId: string | undefined): ProviderUpstreamLimit[] {
  const { data: providers = [] } = useCdnProviders(serviceId ?? '');

  if (!serviceId || providers.length === 0) return [];

  return providers
    .filter(
      (p) =>
        // Only enabled (active) secondaries — the canonical handles its own limits
        // through the core write path, and suspended providers receive no writes.
        !p.is_canonical &&
        p.status === 'active' &&
        (p.capabilities?.upstream_unsupported_fields?.length ?? 0) > 0,
    )
    .map((p) => ({
      providerName: p.provider_name,
      unsupportedFields: p.capabilities!.upstream_unsupported_fields,
    }));
}

interface ToggleParams {
  provider: string;
  enabled: boolean;
}

export function useToggleCdnProvider(serviceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ provider, enabled }: ToggleParams): Promise<ToggleProviderResult> => {
      const res = await serviceApiV3.put<ToggleProviderResult>(
        `/services/${serviceId}/cdn/providers/${provider}/status`,
        { enabled },
      );
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: cdnProviderKeys.providers(serviceId) });
      // Invalidate all umbrella queries regardless of projectId filter.
      qc.invalidateQueries({ queryKey: ['cdn', 'umbrellas'] });
    },
  });
}
